import { Content, ContentType, Lesson, SllLesson, SllModule } from '../../types';
import { useCallback, useMemo } from 'react';
import { useStudentLessonContent } from '@src/hooks/useStudentLessonContent';
import { useAnalytics, Events, useUser } from '@hooks';
type ContentElements = {
  contentId: string;
  lessonId?: string;
  content: SllModule | Content;
  lesson?: SllLesson | Lesson;
}
type ContentArr = {
  [lessonId: string]: ContentElements[];
};

function selectAdditionContent(modules: any[]) {
  if (modules.length > 0) {
    if (modules.at(0).content.type === ContentType.FORM || modules.at(0).content.name.toLowerCase().includes('exit ticket')) {
      modules.shift()
      return selectAdditionContent(modules)
    } else {
      return modules.at(0);
    }
  } else {
    return undefined
  }
}

export const useStudentNextContent = () => {
  const { content, lesson, sllModule, sllLesson, setSllLesson, setSllModule, setLesson, setContent } = useStudentLessonContent();
  const { trackEvent } = useAnalytics();
  const [user] = useUser();

  const contentArray: ContentArr = useMemo(() => {
    const res: ContentArr = {};

    if (sllLesson?.id) {
      let modules = [...sllLesson?.presentation || []]

      modules.forEach(c => {
        if (!res[sllLesson?.id]) {
          res[sllLesson?.id] = [];
        }
        res[sllLesson?.id].push({
          contentId: c?.id || '',
          lessonId: sllLesson.id,
          content: c as SllModule,
          lesson: sllLesson,
        })
      })
    } else if (lesson?.id) {
      let modules = [...lesson.modules?.filter(module => module.data) || []]

      modules.forEach(c => {
        if (!res[lesson?.id]) {
          res[lesson?.id] = [];
        }
        res[lesson?.id].push({
          contentId: c?.id || '',
          lessonId: lesson.id,
          content: c as Content,
          lesson: lesson,
        })
      })
    }
    return res;
  }, [sllLesson, lesson]);

  const nextContent = useMemo(() => {
    let arr: ContentElements[] = [];
    if (sllLesson) {
      if (!contentArray || !sllModule) return undefined;
      arr = contentArray[sllLesson.id];
    } else if (lesson) {
      if (!contentArray || !lesson) return undefined;
      arr = contentArray[lesson.id];
    }
    if (!arr) return undefined
    if (sllModule) {
      const elIndex = arr.findIndex(c => c.contentId === sllModule.id);
      if (elIndex === -1) {
        return undefined;
      }
      return elIndex === arr.length - 1 ? undefined : arr[elIndex]?.content;
    }
    if (content) {
      const elIndex = arr.findIndex(c => c.contentId === content.id);
      if (elIndex === -1) {
        return undefined;
      }
      return elIndex === arr.length - 1 ? undefined : arr[elIndex]?.content;
    }
  }, [contentArray, sllLesson, lesson, sllModule, content]);

  const hasNextContent = !!nextContent;

  const hasPrevContent = useMemo(() => {
    let arr: ContentElements[] = [];
    if (sllLesson) {
      if (!contentArray || !sllModule) return false;
      arr = contentArray[sllLesson.id];
    } else if (lesson) {
      if (!contentArray || !content) return false;
      arr = contentArray[lesson.id];
    }
    if (!arr) return false
    if (!!sllModule) {
      const elIndex = arr.findIndex(c => c.contentId === sllModule.id);
      return elIndex > 0;  
    } else if (!!content) {
      const elIndex = arr.findIndex(c => c.contentId === content.id);
      return elIndex > 0;
    }
  }, [contentArray, sllLesson, sllModule, content, lesson]);

  const hasAdditionalContent = useMemo(() => {
    let arr: ContentElements[] = [];
    if (sllLesson) {
      if (!contentArray || !sllModule) return false;
      arr = contentArray[sllLesson.id];
    } else if (lesson) {
      if (!contentArray || !content) return false;
      arr = contentArray[lesson.id];
    }
    if (!arr || !sllModule) return false

    const currentIndex = arr.findIndex(c => (c.contentId === sllModule?.id)) + 1;
    const remainModules = arr.slice(currentIndex)
    var addition = selectAdditionContent(remainModules);

    if (!addition) return false

    if (addition.content.lastInLesson !== true) {
      return true
    }
    return false
  }, [contentArray, sllLesson, sllModule, lesson, content]);

  const selectNext = useCallback((currentContent?: SllModule | Content) => {
    const contentObj = currentContent || sllModule || content;
    if (!contentObj) return;
    let arr: ContentElements[] = [];
    if (sllLesson) {
      arr = contentArray[sllLesson.id];
    } else if (lesson) {
      arr = contentArray[lesson.id];
    }
    
    if (!arr) return false
    const currentIndex = arr.findIndex(c => c.contentId === contentObj.id) + 1;
    const next = arr[currentIndex];
    if (!!next) {
      if (next.content.__typename === 'SLLModule') {
        setSllModule(next.content as SllModule);  
      } else if (next.content.__typename === 'Content') {
        setContent(next.content as Content);  
      }
      if (next.lesson?.__typename === 'SLLLesson') {
        setSllLesson(next.lesson as SllLesson);  
      } else if (next.lesson?.__typename === 'Lesson') {
        setLesson(next.lesson as Lesson);  
      }

      trackEvent(Events.actions.lessonModulesViewed, {
        standalone_lesson: true,
        lesson_name: next.lesson?.name,
        module_name: next.content?.name,
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
    }
  }, [sllModule, sllLesson, content, lesson, contentArray, setSllModule, setSllLesson, trackEvent, setLesson, setContent, user]);

  const selectExitTicket = useCallback((currentContent?: SllModule | Content) => {
    const contentObj = currentContent || sllModule;
    if (!contentObj) return;
    let arr: ContentElements[] = [];
    if (sllLesson) {
      arr = contentArray[sllLesson.id];
    } else if (lesson) {
      arr = contentArray[lesson.id];
    }
    
    if (!arr) return false
    const exitTicketIndex = arr.findIndex(c => c.content.type === ContentType.FORM || c.content.name.toLowerCase().includes('exit ticket'));
    const next = arr[exitTicketIndex];
    if (!!next) {
      if (next.content.__typename === 'SLLModule') {
        setSllModule(next.content as SllModule);  
      } else if (next.content.__typename === 'Content') {
        setContent(next.content as Content);  
      }
      if (next.lesson?.__typename === 'SLLLesson') {
        setSllLesson(next.lesson as SllLesson);  
      } else if (next.lesson?.__typename === 'Lesson') {
        setLesson(next.lesson as Lesson);  
      }
      
      trackEvent(Events.actions.lessonModulesViewed, {
        standalone_lesson: true,
        lesson_name: next.lesson?.name,
        module_name: next.content?.name,
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
    }
  }, [sllModule, sllLesson, lesson, contentArray, setSllModule, setSllLesson, trackEvent, setLesson, setContent, user]);

  const selectAdditionalContent = useCallback((currentContent?: SllModule | Content) => {
    const contentObj = currentContent || sllModule || content;
    if (!contentObj) return;
    let arr: ContentElements[] = [];
    if (sllLesson) {
      arr = contentArray[sllLesson.id];
    } else if (lesson) {
      arr = contentArray[lesson.id];
    }
    
    if (!arr) return false
    const currentIndex = arr.findIndex(c => (c.contentId === contentObj.id)) + 1;
    const remainModules = arr.slice(currentIndex)
    var next = selectAdditionContent(remainModules);
    if (!!next) {
      if (next.content.__typename === 'SLLModule') {
        setSllModule(next.content as SllModule);  
      } else if (next.content.__typename === 'Content') {
        setContent(next.content as Content);  
      }
      if (next.lesson?.__typename === 'SLLLesson') {
        setSllLesson(next.lesson as SllLesson);  
      } else if (next.lesson?.__typename === 'Lesson') {
        setLesson(next.lesson as Lesson);  
      }

      trackEvent(Events.actions.lessonModulesViewed, {
        standalone_lesson: true,
        lesson_name: next.lesson?.name,
        module_name: next.content?.name,
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
    }
  }, [sllModule, sllLesson, content, lesson, contentArray, setSllModule, setSllLesson, setContent, setLesson, trackEvent, user]);

  const selectPrev = useCallback(() => {
    if (!sllModule && !content) return;
    let arr: ContentElements[] = [];
    if (sllLesson) {
      arr = contentArray[sllLesson.id];
    } else if (lesson) {
      arr = contentArray[lesson.id];
    }
    
    // if (!arr || !sllModule) return false
    const currentIndex = arr.findIndex(c => c.contentId === (sllModule?.id || content?.id));
    const next = arr[currentIndex - 1];

    if (next) {
      if (next.content.__typename === 'SLLModule') {
        setSllModule(next.content as SllModule);  
      } else if (next.content.__typename === 'Content') {
        setContent(next.content as Content);  
      }
      if (next.lesson?.__typename === 'SLLLesson') {
        setSllLesson(next.lesson as SllLesson);  
      } else if (next.lesson?.__typename === 'Lesson') {
        setLesson(next.lesson as Lesson);  
      }
      
      trackEvent(Events.actions.lessonModulesViewed, {
        standalone_lesson: true,
        lesson_name: next.lesson?.name,
        module_name: next.content?.name,
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      });
    }
  }, [content, lesson, sllModule, sllLesson, contentArray, setSllModule, setSllLesson, trackEvent, setLesson, setContent, user]);

  return {
    hasNextContent,
    hasPrevContent,
    hasAdditionalContent,
    selectNext,
    selectPrev,
    selectExitTicket,
    selectAdditionalContent,
    nextContent,
    sllModule,
  };
}
